import { createClient, type Session } from "@supabase/supabase-js";

const supabaseUrl = import.meta.env["VITE_SUPABASE_URL"] as string | undefined;
const supabaseAnonKey = import.meta.env["VITE_SUPABASE_ANON_KEY"] as string | undefined;
const isTestEnv = import.meta.env["MODE"] === "test" || import.meta.env["VITEST"] === "true";

if (!isTestEnv && (!supabaseUrl || !supabaseAnonKey)) {
  console.warn(
    "[LucraLink] Variáveis VITE_SUPABASE_URL e/ou VITE_SUPABASE_ANON_KEY ausentes. Operando em modo de contingência.",
  );
}

// In test environment, use placeholders to allow importing without env vars.
// The real client is never called in tests since the module is mocked via vi.mock.
export const supabase = createClient(
  supabaseUrl ?? "https://placeholder.supabase.co",
  supabaseAnonKey ?? "placeholder-anon-key",
);

/**
 * Event dispatched on window when session recovery hits the timeout,
 * indicating the auth backend is unreachable. UI components (e.g.
 * BackendStatusBanner) listen for this event to show a warning to the user.
 */
export const BACKEND_UNAVAILABLE_EVENT = "lucralink:auth-backend-unavailable";

const SESSION_TIMEOUT_MS = 4000;

/**
 * Fetches the current session with a timeout. supabase-js can get stuck in
 * internal retry (AuthRetryableFetchError) when the backend is unreachable,
 * and getSession()/onAuthStateChange never resolve in that scenario. That
 * hangs the UI (infinite spinner) in both AuthProvider and the route guards.
 *
 * ponytail: the real supabase-js promise keeps running in the background
 * after the timeout (no way to cancel it) - it's just ignored here. Upgrade
 * if we ever need to abort the underlying HTTP request.
 */
const DEMO_STORAGE_KEY = "lucralink_demo_session";

/** Modo demonstracao (sessao fake, sem backend) so existe em build de
 *  desenvolvimento com VITE_ENABLE_DEMO_MODE=true. Em producao e sempre off. */
export function isDemoModeAvailable(): boolean {
  return import.meta.env["DEV"] === true && import.meta.env["VITE_ENABLE_DEMO_MODE"] === "true";
}

function readDemoFlag(): boolean {
  try {
    return localStorage.getItem(DEMO_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

function clearDemoFlag(): void {
  try {
    localStorage.removeItem(DEMO_STORAGE_KEY);
  } catch {
    // Storage bloqueado: nao ha flag para limpar.
  }
}

export function isDemoMode(): boolean {
  if (typeof window === "undefined") return false;
  if (!isDemoModeAvailable()) {
    // Flag velha de um build de dev nunca liga a sessao fake em producao.
    if (readDemoFlag()) clearDemoFlag();
    return false;
  }
  return readDemoFlag();
}

export function enableDemoMode(): void {
  if (typeof window === "undefined" || !isDemoModeAvailable()) return;
  try {
    localStorage.setItem(DEMO_STORAGE_KEY, "true");
  } catch {
    // Storage bloqueado: o modo demonstracao simplesmente nao liga.
  }
}

export function disableDemoMode(): void {
  if (typeof window !== "undefined") clearDemoFlag();
}

const DEMO_USER_SESSION: Session = {
  access_token: "demo-token",
  refresh_token: "demo-refresh",
  expires_in: 86400,
  token_type: "bearer",
  user: {
    id: "demo-user-id",
    app_metadata: {},
    user_metadata: { name: "Usuário Demo" },
    aud: "authenticated",
    created_at: new Date().toISOString(),
    email: "demo@lucralink.com.br",
  },
};

export async function getSessionSafe(
  timeoutMs: number = SESSION_TIMEOUT_MS,
): Promise<Session | null> {
  if (isDemoMode()) {
    return DEMO_USER_SESSION;
  }

  const realSession = supabase.auth.getSession().then(({ data }) => ({
    session: data.session,
    timedOut: false as const,
  }));

  const timeout = new Promise<{ session: null; timedOut: true }>((resolve) => {
    setTimeout(() => resolve({ session: null, timedOut: true }), timeoutMs);
  });

  const result = await Promise.race([realSession, timeout]);

  if (result.timedOut && typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(BACKEND_UNAVAILABLE_EVENT));
  }

  return result.session;
}
